import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    FiArrowRight,
    FiCheckCircle,
    FiPhoneCall,
    FiMapPin,
    FiAward,
    FiPlay,
    FiX,
    FiVideo,
    FiAlertCircle,
    FiRefreshCw,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useLightbox } from '../../context/LightboxContext';
import { BUSINESS_WHATSAPP_CHAT_URL } from '../../config/businessInfo';
import { REAL_SHOP_IMAGES } from '../../config/realMedia';
import showroomTourVideoService from '../../services/showroomTourVideoService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import { classifyAspectRatio } from '../../utils/videoUtils';
import './HeroSection.css';

// Authentic Rahber Corporation Flagship Storefront Image
const SHOWROOM_HERO_IMG = REAL_SHOP_IMAGES.storefront;
const SHOWROOM_HERO_BACKUP = REAL_SHOP_IMAGES.refrigeratorAisle;

const HeroSection = () => {
    const { openLightbox } = useLightbox();
    const [heroImgSrc, setHeroImgSrc] = useState(SHOWROOM_HERO_IMG);
    const [isLoaded, setIsLoaded] = useState(false);

    // Dynamic Showroom Tour Video state (no hardcoded or dummy fallback video)
    const [tourVideo, setTourVideo] = useState(null);
    const [videoLoading, setVideoLoading] = useState(true);
    const [videoError, setVideoError] = useState('');
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [isIframeLoading, setIsIframeLoading] = useState(false);
    const [useFallbackRawSrc, setUseFallbackRawSrc] = useState(false);
    const [playbackError, setPlaybackError] = useState('');
    const [detectedOrientation, setDetectedOrientation] = useState('unknown');

    const videoRef = useRef(null);
    const isMountedRef = useRef(true);
    const latestFetchIdRef = useRef(0);
    const lastHiddenTimestampRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            showroomTourVideoService.cancelActiveMetadataProbe();
        };
    }, []);

    const handleImgError = () => {
        if (heroImgSrc !== SHOWROOM_HERO_BACKUP) {
            setHeroImgSrc(SHOWROOM_HERO_BACKUP);
        }
    };

    const fetchTourVideo = useCallback(
        async ({ showLoading = true, forceRefresh = false } = {}) => {
            const fetchId = ++latestFetchIdRef.current;

            try {
                if (showLoading) {
                    setVideoLoading(true);
                }
                setVideoError('');

                const nextVideo =
                    await showroomTourVideoService.getShowroomTourVideo({
                        forceRefresh,
                    });

                if (
                    !isMountedRef.current ||
                    fetchId !== latestFetchIdRef.current
                ) {
                    return;
                }

                const cachedMeta = nextVideo?.videoSrc
                    ? showroomTourVideoService.getCachedVideoMetadata(
                          nextVideo.videoSrc
                      ) ||
                      showroomTourVideoService.getCachedVideoMetadata(
                          nextVideo.rawVideoSrc
                      )
                    : null;

                setTourVideo((prevVideo) => {
                    if (!nextVideo) {
                        return null;
                    }

                    // Preserve exact object reference when video config is unchanged so active
                    // playback is never interrupted or re-rendered by tab-focus or SSE checks
                    if (
                        prevVideo &&
                        prevVideo.playbackKey === nextVideo.playbackKey &&
                        prevVideo.videoSrc === nextVideo.videoSrc &&
                        prevVideo.rawVideoSrc === nextVideo.rawVideoSrc &&
                        prevVideo.embedUrl === nextVideo.embedUrl &&
                        prevVideo.posterUrl === nextVideo.posterUrl &&
                        prevVideo.title === nextVideo.title &&
                        prevVideo.badge === nextVideo.badge &&
                        prevVideo.buttonText === nextVideo.buttonText
                    ) {
                        return prevVideo;
                    }

                    const prevKey = prevVideo?.playbackKey || '';
                    const nextKey = nextVideo.playbackKey || '';
                    if (prevKey !== nextKey) {
                        setPlaybackError('');
                        setUseFallbackRawSrc(false);
                        setDetectedOrientation(
                            cachedMeta?.orientation ||
                                nextVideo.initialOrientation ||
                                'unknown'
                        );
                    }

                    return nextVideo;
                });

                if (!nextVideo && videoRef.current) {
                    try {
                        videoRef.current.pause();
                    } catch (_) {}
                }
            } catch (err) {
                if (
                    !isMountedRef.current ||
                    fetchId !== latestFetchIdRef.current
                ) {
                    return;
                }

                if (showLoading) {
                    setTourVideo(null);
                    setVideoError(
                        err?.message ||
                            'Unable to load showroom tour video right now.'
                    );
                }
            } finally {
                if (
                    isMountedRef.current &&
                    fetchId === latestFetchIdRef.current &&
                    showLoading
                ) {
                    setVideoLoading(false);
                }
            }
        },
        []
    );

    useEffect(() => {
        fetchTourVideo({ showLoading: true, forceRefresh: false });
    }, [fetchTourVideo]);

    // Real-time SSE synchronization so admin uploads, replacements, or removals reflect immediately
    const handleRealtimeTourVideoUpdate = useCallback(() => {
        fetchTourVideo({ showLoading: false, forceRefresh: true });
    }, [fetchTourVideo]);

    useRealtimeSync('showroom_tour_video', handleRealtimeTourVideoUpdate);
    useRealtimeSync('showroom-tour-video', handleRealtimeTourVideoUpdate);
    useRealtimeSync('showroom_video', handleRealtimeTourVideoUpdate);
    useRealtimeSync('settings', handleRealtimeTourVideoUpdate);
    useRealtimeSync('media', handleRealtimeTourVideoUpdate);

    // Re-sync silently when user returns to the browser tab
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                lastHiddenTimestampRef.current = Date.now();
                return;
            }
            if (document.visibilityState === 'visible') {
                const elapsed = Date.now() - lastHiddenTimestampRef.current;
                if (lastHiddenTimestampRef.current > 0 && elapsed >= 5000) {
                    fetchTourVideo({ showLoading: false, forceRefresh: true });
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () =>
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
    }, [fetchTourVideo]);

    // Active resolved native video source (falls back cleanly to rawVideoSrc if CDN transform fails)
    const activeNativeVideoSrc =
        tourVideo?.playerType === 'native'
            ? useFallbackRawSrc && tourVideo.rawVideoSrc
                ? tourVideo.rawVideoSrc
                : tourVideo.videoSrc || tourVideo.rawVideoSrc
            : null;

    // Warm media origin connection & pre-probe video metadata during browser idle time
    // so orientation and container seek headers (`moov` atom) are ready BEFORE click
    useEffect(() => {
        if (!tourVideo) return undefined;

        const primaryUrl = activeNativeVideoSrc || tourVideo.embedUrl;
        if (primaryUrl) {
            showroomTourVideoService.preconnectMediaOrigin(primaryUrl);
        }
        if (tourVideo.posterUrl) {
            showroomTourVideoService.preconnectMediaOrigin(tourVideo.posterUrl);
        }

        if (tourVideo.playerType !== 'native' || !activeNativeVideoSrc) {
            return undefined;
        }

        const cached =
            showroomTourVideoService.getCachedVideoMetadata(
                activeNativeVideoSrc
            );
        if (cached && cached.orientation) {
            setDetectedOrientation((prev) =>
                prev === cached.orientation ? prev : cached.orientation
            );
            return undefined;
        }

        if (isVideoModalOpen) {
            return undefined;
        }

        let cancelled = false;
        let timerId = null;
        let idleId = null;

        const runMetadataProbe = () => {
            if (cancelled) return;
            showroomTourVideoService
                .probeVideoMetadata(activeNativeVideoSrc)
                .then((meta) => {
                    if (
                        !cancelled &&
                        isMountedRef.current &&
                        meta &&
                        meta.orientation
                    ) {
                        setDetectedOrientation((prev) =>
                            prev === meta.orientation ? prev : meta.orientation
                        );
                    }
                });
        };

        if (
            typeof window !== 'undefined' &&
            typeof window.requestIdleCallback === 'function'
        ) {
            idleId = window.requestIdleCallback(runMetadataProbe, {
                timeout: 1200,
            });
        } else {
            timerId = setTimeout(runMetadataProbe, 350);
        }

        return () => {
            cancelled = true;
            if (
                idleId !== null &&
                typeof window !== 'undefined' &&
                typeof window.cancelIdleCallback === 'function'
            ) {
                window.cancelIdleCallback(idleId);
            }
            if (timerId !== null) {
                clearTimeout(timerId);
            }
        };
    }, [tourVideo, activeNativeVideoSrc, isVideoModalOpen]);

    // Pre-warm connection & metadata immediately on pointer hover/focus/touch before click
    const handleTriggerWarmup = useCallback(() => {
        if (!tourVideo || isVideoModalOpen) return;

        const targetUrl = activeNativeVideoSrc || tourVideo.embedUrl;
        if (targetUrl) {
            showroomTourVideoService.preconnectMediaOrigin(targetUrl);
        }

        if (tourVideo.playerType === 'native' && activeNativeVideoSrc) {
            const cached =
                showroomTourVideoService.getCachedVideoMetadata(
                    activeNativeVideoSrc
                );
            if (!cached) {
                showroomTourVideoService
                    .probeVideoMetadata(activeNativeVideoSrc)
                    .then((meta) => {
                        if (
                            isMountedRef.current &&
                            meta &&
                            meta.orientation
                        ) {
                            setDetectedOrientation((prev) =>
                                prev === meta.orientation
                                    ? prev
                                    : meta.orientation
                            );
                        }
                    });
            }
        }
    }, [tourVideo, activeNativeVideoSrc, isVideoModalOpen]);

    const closeVideoModal = useCallback(() => {
        if (videoRef.current) {
            try {
                videoRef.current.pause();
            } catch (_) {}
        }
        setIsVideoModalOpen(false);
        setIsIframeLoading(false);
    }, []);

    // Lock scroll, pause background marquee animations for dedicated GPU video decoding, and handle Escape key
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isVideoModalOpen) {
                closeVideoModal();
            }
        };

        if (isVideoModalOpen) {
            document.body.style.overflow = 'hidden';
            document.body.classList.add('showroom-video-modal-open');
            window.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = '';
            document.body.classList.remove('showroom-video-modal-open');
        }

        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('showroom-video-modal-open');
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isVideoModalOpen, closeVideoModal]);

    // Ensure programmatic smooth start once the native <video> element mounts in the modal
    useEffect(() => {
        if (
            !isVideoModalOpen ||
            tourVideo?.playerType !== 'native' ||
            !activeNativeVideoSrc
        ) {
            return undefined;
        }

        const videoEl = videoRef.current;
        if (!videoEl) return undefined;

        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
                // If strict mobile browser policy requires direct interaction on native controls,
                // keep native controls ready without surfacing a false error state.
            });
        }

        return undefined;
    }, [isVideoModalOpen, tourVideo?.playerType, activeNativeVideoSrc]);

    const openVideoModal = (e) => {
        if (e) e.stopPropagation();
        if (!tourVideo) return;

        // Release any background probe so modal <video preload="auto"> gets exclusive stream priority
        showroomTourVideoService.cancelActiveMetadataProbe();

        // Pause any other playing HTML5 videos on the page
        if (typeof document !== 'undefined') {
            document.querySelectorAll('video').forEach((v) => {
                if (!v.paused) {
                    try {
                        v.pause();
                    } catch (_) {}
                }
            });
        }

        const cachedMeta = activeNativeVideoSrc
            ? showroomTourVideoService.getCachedVideoMetadata(
                  activeNativeVideoSrc
              )
            : null;

        setPlaybackError('');
        setIsIframeLoading(tourVideo.playerType !== 'native');
        setDetectedOrientation(
            cachedMeta?.orientation ||
                tourVideo.initialOrientation ||
                'unknown'
        );
        setIsVideoModalOpen(true);
    };

    const handleVideoLoadedMetadata = (e) => {
        const el = e.currentTarget;
        if (el && el.videoWidth > 0 && el.videoHeight > 0) {
            if (activeNativeVideoSrc) {
                showroomTourVideoService.recordVideoMetadata(
                    activeNativeVideoSrc,
                    el.videoWidth,
                    el.videoHeight
                );
            }
            const profile = classifyAspectRatio(el.videoWidth, el.videoHeight);
            if (profile && profile.orientation) {
                setDetectedOrientation((prev) =>
                    prev === profile.orientation ? prev : profile.orientation
                );
            }
        }
    };

    const handleVideoPlaybackError = () => {
        // If an optimized CDN URL failed due to restricted transforms, transparently fall back to rawVideoSrc
        if (
            tourVideo?.playerType === 'native' &&
            !useFallbackRawSrc &&
            tourVideo.rawVideoSrc &&
            tourVideo.videoSrc !== tourVideo.rawVideoSrc
        ) {
            setUseFallbackRawSrc(true);
            return;
        }

        setIsIframeLoading(false);
        setPlaybackError(
            'Unable to play the showroom tour video. Please check your connection or try again.'
        );
    };

    const handleRetryVideo = () => {
        setPlaybackError('');
        setUseFallbackRawSrc(false);
        if (isVideoModalOpen && tourVideo?.playerType !== 'native') {
            setIsIframeLoading(true);
        }
        fetchTourVideo({ showLoading: true, forceRefresh: true });
    };

    // Resolve active player orientation (defaults widescreen 16:9 when unknown so walkthroughs never jump on open)
    const activeOrientation =
        detectedOrientation && detectedOrientation !== 'unknown'
            ? detectedOrientation
            : tourVideo?.initialOrientation &&
                tourVideo.initialOrientation !== 'unknown'
              ? tourVideo.initialOrientation
              : 'landscape';

    const modalCardModifier =
        activeOrientation === 'landscape'
            ? 'hero-video-modal-card--landscape'
            : activeOrientation === 'square'
              ? 'hero-video-modal-card--square'
              : '';

    const playerContainerModifier =
        activeOrientation === 'landscape'
            ? 'hero-video-player-container--landscape'
            : activeOrientation === 'square'
              ? 'hero-video-player-container--square'
              : '';

    const renderShowroomVideoBar = () => {
        if (videoLoading && !tourVideo) {
            return (
                <div className="hero-video-bar-below">
                    <div
                        className="hero-video-status-pill hero-video-status-pill--loading"
                        role="status"
                        aria-live="polite"
                    >
                        <span
                            className="hero-video-spinner"
                            aria-hidden="true"
                        />
                        <span>Loading Showroom Tour Video...</span>
                    </div>
                </div>
            );
        }

        if (videoError && !tourVideo) {
            return (
                <div className="hero-video-bar-below">
                    <div
                        className="hero-video-status-pill hero-video-status-pill--error"
                        role="alert"
                    >
                        <FiAlertCircle
                            className="hero-video-status-icon"
                            aria-hidden="true"
                        />
                        <span>Unable to load tour video</span>
                        <button
                            type="button"
                            className="hero-video-retry-inline-btn"
                            onClick={handleRetryVideo}
                        >
                            <FiRefreshCw aria-hidden="true" />
                            <span>Retry</span>
                        </button>
                    </div>
                </div>
            );
        }

        if (!tourVideo) {
            return (
                <div className="hero-video-bar-below">
                    <div
                        className="hero-video-status-pill hero-video-status-pill--empty"
                        role="status"
                    >
                        <FiVideo
                            className="hero-video-status-icon"
                            aria-hidden="true"
                        />
                        <span>Showroom tour video not available</span>
                    </div>
                </div>
            );
        }

        return (
            <div className="hero-video-bar-below">
                <button
                    type="button"
                    className="hero-video-trigger-btn"
                    onClick={openVideoModal}
                    onMouseEnter={handleTriggerWarmup}
                    onFocus={handleTriggerWarmup}
                    onTouchStart={handleTriggerWarmup}
                    aria-label="Play Showroom Tour Video"
                    title={
                        tourVideo.title || 'Watch Official Showroom Tour Video'
                    }
                >
                    <span className="hero-video-trigger-icon-wrap">
                        <FiPlay className="hero-video-trigger-icon" />
                    </span>
                    <span className="hero-video-trigger-text">
                        {tourVideo.buttonText || 'Watch Showroom Tour Video'}
                    </span>
                </button>
            </div>
        );
    };

    return (
        <section id="hero-section" className="hero-section hero-section--modern" aria-label="Hero showcase">
            <div className="container">
                <div className="hero-grid">
                    {/* Left Column: Text, Value Props & CTAs */}
                    <div className="hero-text-col">
                        {/* Mobile-only Showroom Media (Top of hero for mobile: Picture then Button) */}
                        <div className="hero-media-mobile-wrap">
                            <div
                                className={`hero-media-frame hero-media-frame--mobile zoomable-image ${isLoaded ? 'loaded' : 'loading'}`}
                                onClick={() => openLightbox(heroImgSrc, 'Rahber Corporation Flagship Showroom in Lahore', 'Lahore Flagship Showroom: Main Bazar Chandni Chowk, Sanda Kalan')}
                                title="Tap to view full showroom photo"
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        openLightbox(heroImgSrc, 'Rahber Corporation Flagship Showroom in Lahore', 'Lahore Flagship Showroom: Main Bazar Chandni Chowk, Sanda Kalan');
                                    }
                                }}
                            >
                                {!isLoaded && (
                                    <div className="hero-media-skeleton" aria-hidden="true">
                                        <div className="hero-media-skeleton-pulse" />
                                        <span className="hero-media-skeleton-text">Rahber Flagship Showroom</span>
                                    </div>
                                )}
                                <img
                                    src={heroImgSrc}
                                    alt="Rahber Corporation Premium Electronics Showroom in Lahore"
                                    className={`hero-media-img ${isLoaded ? 'hero-media-img--visible' : 'hero-media-img--hidden'}`}
                                    loading="eager"
                                    decoding="async"
                                    fetchpriority="high"
                                    onLoad={() => setIsLoaded(true)}
                                    onError={handleImgError}
                                />
                            </div>

                            {/* Dynamic Showroom Tour Video Trigger / State Bar Placed BELOW Image */}
                            {renderShowroomVideoBar()}
                        </div>

                        {/* Caption Eyebrow Badge (placed cleanly after pic and button on mobile) */}
                        <div className="hero-eyebrow-wrap">
                            <span className="hero-eyebrow">
                                <FiAward className="hero-eyebrow-icon" aria-hidden="true" />
                                <span>Trusted Retailer Since 1980 • Lahore</span>
                            </span>
                        </div>

                        {/* Urdu accent calligraphy tagline (Only visible on desktop/laptop view) */}
                        <div className="hero-urdu-tagline" lang="ur">
                            اصلی برانڈز • آسان اقساط • سچا اعتماد
                        </div>

                        <h1 className="hero-heading">
                            Quality Appliances for{' '}
                            <span className="hero-highlight-word">Everyday Pakistani Living</span>
                        </h1>

                        <p className="hero-description">
                            Explore dependable inverter air conditioners, deep freezers, refrigerators, smart 4K TVs, and essential home appliances. Choose between direct cash purchases with <strong>instant discount</strong> or accessible monthly installment plans designed for your household budget in Lahore.
                        </p>

                        <div className="hero-micro-benefits">
                            <div className="hero-micro-row hero-micro-row--top">
                                <span className="hero-micro-pill">
                                    <FiCheckCircle aria-hidden="true" />
                                    <span>Flexible Installments</span>
                                </span>
                                <span className="hero-micro-pill">
                                    <FiCheckCircle aria-hidden="true" />
                                    <span>Direct Cash (Instant Discount)</span>
                                </span>
                            </div>
                            <div className="hero-micro-row hero-micro-row--bottom">
                                <span className="hero-micro-pill hero-micro-pill--call">
                                    <FiPhoneCall aria-hidden="true" />
                                    <span>Call Confirmation on Orders</span>
                                </span>
                            </div>
                        </div>

                        {/* Equal Balanced CTAs matching Rise Up Schools reference */}
                        <div className="hero-actions">
                            <Link to="/products" className="hero-btn hero-btn-primary">
                                <span>Browse Products</span>
                                <FiArrowRight aria-hidden="true" />
                            </Link>
                            <Link to="/installment" className="hero-btn hero-btn-secondary">
                                <span>Buy on Installment</span>
                            </Link>
                        </div>
                    </div>

                    {/* Right Column (Desktop): Premium Showroom Showcase */}
                    <div className="hero-media-col hero-media-desktop-wrap">
                        <div
                            className={`hero-media-frame hero-media-frame--desktop zoomable-image ${isLoaded ? 'loaded' : 'loading'}`}
                            onClick={() => openLightbox(heroImgSrc, 'Rahber Corporation Flagship Showroom in Lahore', 'Lahore Flagship Showroom: Main Bazar Chandni Chowk, Sanda Kalan')}
                            title="Click to view full showroom photo"
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    openLightbox(heroImgSrc, 'Rahber Corporation Flagship Showroom in Lahore', 'Lahore Flagship Showroom: Main Bazar Chandni Chowk, Sanda Kalan');
                                }
                            }}
                        >
                            {!isLoaded && (
                                <div className="hero-media-skeleton" aria-hidden="true">
                                    <div className="hero-media-skeleton-pulse" />
                                    <span className="hero-media-skeleton-text">Rahber Flagship Showroom</span>
                                </div>
                            )}
                            <img
                                src={heroImgSrc}
                                alt="Rahber Corporation Premium Electronics Showroom in Lahore"
                                className={`hero-media-img ${isLoaded ? 'hero-media-img--visible' : 'hero-media-img--hidden'}`}
                                loading="eager"
                                decoding="async"
                                fetchpriority="high"
                                onLoad={() => setIsLoaded(true)}
                                onError={handleImgError}
                            />
                        </div>

                        {/* Dynamic Showroom Tour Video Trigger / State Bar Placed BELOW Image */}
                        {renderShowroomVideoBar()}
                    </div>
                </div>
            </div>

            {/* Centered Smooth Showroom Tour Video Modal */}
            {isVideoModalOpen && (
                <div
                    className="hero-video-modal-backdrop"
                    onClick={closeVideoModal}
                    role="dialog"
                    aria-modal="true"
                    aria-label={
                        tourVideo?.title || 'Official Showroom Video Tour'
                    }
                >
                    <div
                        className={`hero-video-modal-card ${modalCardModifier}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="hero-video-modal-header">
                            <div className="hero-video-modal-title-group">
                                <span className="hero-video-modal-badge">
                                    <FiPlay />{' '}
                                    {tourVideo?.badge ||
                                        'Official Showroom Tour Walkthrough'}
                                </span>
                                <h3 className="hero-video-modal-title">
                                    {tourVideo?.title ||
                                        'Rahber Corporation Flagship Showroom Walkthrough'}
                                </h3>
                            </div>
                            <button
                                type="button"
                                className="hero-video-modal-close-btn"
                                onClick={closeVideoModal}
                                aria-label="Close video"
                                title="Close Video (Esc)"
                            >
                                <FiX />
                            </button>
                        </div>

                        {/* Dynamic Video Player Box */}
                        <div
                            className={`hero-video-player-container ${playerContainerModifier}`}
                        >
                            {videoLoading && !tourVideo ? (
                                <div
                                    className="hero-video-player-state"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <span
                                        className="hero-video-spinner hero-video-spinner--lg"
                                        aria-hidden="true"
                                    />
                                    <p>Loading showroom tour video...</p>
                                </div>
                            ) : videoError || playbackError ? (
                                <div
                                    className="hero-video-player-state hero-video-player-state--error"
                                    role="alert"
                                >
                                    <FiAlertCircle
                                        className="hero-video-player-state-icon"
                                        aria-hidden="true"
                                    />
                                    <p>{playbackError || videoError}</p>
                                    <button
                                        type="button"
                                        className="hero-video-player-retry-btn"
                                        onClick={handleRetryVideo}
                                    >
                                        <FiRefreshCw aria-hidden="true" />
                                        <span>Retry</span>
                                    </button>
                                </div>
                            ) : !tourVideo ? (
                                <div
                                    className="hero-video-player-state"
                                    role="status"
                                >
                                    <FiVideo
                                        className="hero-video-player-state-icon"
                                        aria-hidden="true"
                                    />
                                    <p>
                                        No showroom tour video is currently
                                        available.
                                    </p>
                                </div>
                            ) : tourVideo.playerType === 'native' ? (
                                <video
                                    key={`${tourVideo.playbackKey}-${
                                        useFallbackRawSrc ? 'raw' : 'opt'
                                    }`}
                                    ref={videoRef}
                                    src={activeNativeVideoSrc}
                                    poster={tourVideo.posterUrl || undefined}
                                    controls
                                    autoPlay
                                    playsInline
                                    webkit-playsinline="true"
                                    preload="auto"
                                    className="hero-video-native-element"
                                    onLoadedMetadata={handleVideoLoadedMetadata}
                                    onError={handleVideoPlaybackError}
                                />
                            ) : (
                                <>
                                    {isIframeLoading && (
                                        <div
                                            className="hero-video-player-overlay"
                                            aria-hidden="true"
                                        >
                                            <span className="hero-video-spinner hero-video-spinner--lg" />
                                        </div>
                                    )}
                                    <iframe
                                        key={tourVideo.playbackKey}
                                        src={tourVideo.embedUrl}
                                        title={
                                            tourVideo.title ||
                                            'Official Showroom Video Tour'
                                        }
                                        className="hero-video-iframe-element"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        allowFullScreen
                                        onLoad={() => setIsIframeLoading(false)}
                                        onError={handleVideoPlaybackError}
                                    />
                                </>
                            )}
                        </div>

                        {/* Modal Action Bar */}
                        <div className="hero-video-modal-footer">
                            <div className="hero-video-modal-address">
                                <FiMapPin className="footer-pin-icon" />
                                <span>Main Bazar Chandni Chowk, Sanda Kalan, Lahore</span>
                            </div>
                            <div className="hero-video-modal-actions">
                                <Link
                                    to="/contact"
                                    onClick={closeVideoModal}
                                    className="hero-video-action-btn hero-video-action-btn--primary"
                                    title="Visit Flagship Showroom in Lahore"
                                >
                                    <FiMapPin className="btn-action-icon" aria-hidden="true" />
                                    <span>Visit Showroom</span>
                                </Link>
                                <a
                                    href={BUSINESS_WHATSAPP_CHAT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hero-video-action-btn hero-video-action-btn--wa"
                                    title="Chat with Sales Experts on WhatsApp"
                                >
                                    <FaWhatsapp className="btn-action-icon" aria-hidden="true" />
                                    <span>Chat on WhatsApp</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default HeroSection;
